import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Badge, ScrollArea } from '@creanizer/ui'
import { Search, Trash2, Eye, Edit, Sparkles, Download, Folder } from 'lucide-react'
import { useSkillStore } from '../../../../stores/skillStore'
import { useFolderStore } from '../../../../stores/folderStore'
import type { SavedSkill, SkillInput, SkillResult } from '@creanizer/types'
import { ExportImportButtons } from '../../../../components/common'
import { SkillEditModal } from './SkillEditModal'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface SavedSkillsListProps {
  onLoad: (skill: { input: SkillInput; result: SkillResult }) => void
}

export function SavedSkillsList({ onLoad }: SavedSkillsListProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [editingSkill, setEditingSkill] = useState<SavedSkill | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { skills, removeSkill, clearSkills, exportAsText, exportAsJSON, exportSingleAsText, exportSingleAsJSON, importFromJSON, updateSkill } = useSkillStore()
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // フォルダによるフィルタリング
  let folderFilteredSkills = skills
  if (selectedFolderId === 'all') {
    folderFilteredSkills = skills
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('skill')
    folders.forEach(folder => {
      getItemsInFolder('skill', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredSkills = skills.filter(skill => !allFolderItemIds.has(skill.id))
  } else {
    const itemIds = getItemsInFolder('skill', selectedFolderId)
    folderFilteredSkills = skills.filter(skill => itemIds.includes(skill.id))
  }
  
  // 検索によるフィルタリング
  const filteredSkills = folderFilteredSkills.filter(skill =>
    skill.result.basicInfo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    skill.result.basicInfo.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    skill.result.basicInfo.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (skill.tags && skill.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  )

  const handleExportAsJSON = () => {
    const data = exportAsJSON()
    if (!data) return
    
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `saved_skills_${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済みスキル ({skills.length}/500)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成したスキル・技能設定を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="スキルを検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={handleExportAsJSON}
            onImportJSON={importFromJSON}
            dataTypeName="スキル"
          />

          {skills.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべてのスキルを削除しますか？')) {
                  clearSkills()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="skill"
                items={skills}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('skill', itemId)
                  moveItemBetweenFolders('skill', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* スキルリスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
              <div className="space-y-2 max-w-full w-full">
                {filteredSkills.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {searchTerm ? '検索結果がありません' : '保存済みのスキルがありません'}
                  </p>
                ) : (
                  filteredSkills.map((skill) => (
                    <div 
                      key={skill.id} 
                      className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move hover:shadow-lg transition-shadow"
                      draggable
                      onDragStart={(e) => {
                        setDraggedItemId(skill.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragEnd={() => {
                        setDraggedItemId(null)
                      }}
                      style={{
                        opacity: draggedItemId === skill.id ? 0.5 : 1
                      }}
                    >
                  {/* モバイル版: 縦並びレイアウト */}
                  <div className="flex flex-col gap-3 sm:hidden w-full">
                    <div className="w-full">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{skill.result.basicInfo.name}</h3>
                        {skill.favorite && <Badge variant="secondary" className="text-xs">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">{skill.result.basicInfo.type}</Badge>
                        <Badge variant="outline" className="text-xs">{skill.result.basicInfo.category}</Badge>
                        <Badge variant="outline" className="text-xs">{skill.result.basicInfo.tier}</Badge>
                        <Badge 
                          variant={
                            skill.result.basicInfo.rarity === 'legendary' ? 'destructive' :
                            skill.result.basicInfo.rarity === 'epic' ? 'secondary' :
                            'outline'
                          } 
                          className="text-xs"
                        >
                          {skill.result.basicInfo.rarity}
                        </Badge>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2 truncate">
                        {skill.result.basicInfo.description}
                      </p>
                      
                      {skill.tags && skill.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {skill.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(skill.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2">
                        <FolderMoveSelect
                          generatorType="skill"
                          itemId={skill.id}
                          currentFolderId={getItemFolder('skill', skill.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    {/* モバイル版: 横一列のボタン */}
                    <div className="flex gap-0.5 justify-start w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onLoad({ input: skill.input, result: skill.result })}
                        title="読込"
                        className="w-12 h-7 px-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingSkill(skill)}
                        title="編集"
                        className="w-12 h-7 px-1"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsText(skill.id)}
                        title="テキスト形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">txt</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsJSON(skill.id)}
                        title="JSON形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">json</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このスキルを削除しますか？')) {
                            removeSkill(skill.id)
                          }
                        }}
                        className="w-12 h-7 px-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* PC版: 既存の横並びレイアウト */}
                  <div className="hidden sm:flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{skill.result.basicInfo.name}</h3>
                        {skill.favorite && <Badge variant="secondary">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <span className="truncate">{skill.result.basicInfo.type}</span>
                        <span>•</span>
                        <span className="truncate">{skill.result.basicInfo.category}</span>
                        <span>•</span>
                        <span className="truncate">{skill.result.basicInfo.tier}</span>
                        <span>•</span>
                        <span className={`truncate ${
                          skill.result.basicInfo.rarity === 'legendary' ? 'text-destructive' :
                          skill.result.basicInfo.rarity === 'epic' ? 'text-secondary' :
                          ''
                        }`}>
                          {skill.result.basicInfo.rarity}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {skill.result.basicInfo.description}
                      </p>
                      
                      {skill.tags && skill.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {skill.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(skill.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択（PC版） */}
                      <div className="mt-2 w-32">
                        <FolderMoveSelect
                          generatorType="skill"
                          itemId={skill.id}
                          currentFolderId={getItemFolder('skill', skill.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onLoad({ input: skill.input, result: skill.result })}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          読込
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsText(skill.id)}
                          title="テキスト形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          txt
                        </Button>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingSkill(skill)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          編集
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsJSON(skill.id)}
                          title="JSON形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          json
                        </Button>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このスキルを削除しますか？')) {
                            removeSkill(skill.id)
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {skills.length >= 450 && (
          <p className="text-xs text-muted-foreground text-center">
            保存可能件数の上限（500件）に近づいています
          </p>
        )}
      </div>
      
      {editingSkill && (
        <SkillEditModal
          isOpen={!!editingSkill}
          onClose={() => setEditingSkill(null)}
          onSave={(updatedResult) => {
            updateSkill(editingSkill.id, {
              result: { ...editingSkill.result, ...updatedResult },
              updatedAt: new Date().toISOString()
            })
            setEditingSkill(null)
          }}
          skillResult={editingSkill.result}
          title={`「${editingSkill.result.basicInfo.name}」を編集`}
        />
      )}
    </div>
  )
}